import { createStore, type Store, type StoreActionApi } from 'react-sweet-state';

import {
	isStreamAnswerPart,
	isStreamReasoningPart,
	isStreamResearchBeginningTrace,
	isStreamResearchPlan,
	isStreamResearchStepCompleted,
	isStreamResearchStepContent,
	isStreamResearchStepDocument,
	isStreamResearchStepIcons,
	isStreamResearchStepSource,
	isStreamResearchStepTitle,
	isStreamResearchStepTitleShort,
	isStreamResponse,
	isStreamResponseRedaction,
	isStreamSkillEnd,
	isStreamSkillStart,
	isStreamThinkDeeperTriggered,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { addResearchPlan } from './addResearchPlan';
import { appendStepContent } from './appendStepContent';
import { appendStepDocument } from './appendStepDocument';
import { ARTIFICIAL_PROGRESS_RATE } from './ARTIFICIAL_PROGRESS_RATE';
import { ceiledIncrement } from './ceiledIncrement';
import { clearResearchPlanContent } from './clearResearchPlanContent';
import { completeAllSteps } from './completeAllSteps';
import { initResearchMessage } from './initResearchMessage';
import { markAnswerPartReceived } from './markAnswerPartReceived';
import type { MessageResearch } from './MessageResearch';
import { resetHasReceivedAnswerPart } from './resetHasReceivedAnswerPart';
import { setHasThinkDeeperTriggered } from './setHasThinkDeeperTriggered';
import { setIsDeepResearch } from './setIsDeepResearch';
import { setStepSourceIcons } from './setStepSourceIcons';
import { setStepSources } from './setStepSources';
import { setStepTitle } from './setStepTitle';
import { STARTING_PROGRESS } from './STARTING_PROGRESS';
import type { ChatMessageResearchStore } from './types';
import { updateStep } from './updateStep';

export const store: Store<
	ChatMessageResearchStore,
	{
		resetHasReceivedAnswerPart: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
		addImplicitResearchPlan: ({
			messageId,
			content,
			roundId,
		}: {
			messageId: string;
			content: string;
			roundId: number;
		}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
		processStreamResearch: ({
			messageId,
			streamMessage,
			isDeepResearch,
		}: {
			messageId: string;
			streamMessage: StreamMessage;
			isDeepResearch?: boolean;
		}) => ({ dispatch, getState }: StoreActionApi<ChatMessageResearchStore>) => void;
		updateResearchMessageId: ({
			messageId,
			newMessageId,
		}: {
			messageId: string;
			newMessageId: string;
		}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
		incrementProgressArtificially: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
		markIncompleteResearchesErrored: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
		completeAllSteps: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
		getMessageResearchById: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ getState }: StoreActionApi<ChatMessageResearchStore>) => MessageResearch | undefined;
		clearMessageResearch: ({
			messageId,
		}: {
			messageId: string;
		}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
	}
> = createStore({
	initialState: {} as ChatMessageResearchStore,
	actions: {
		resetHasReceivedAnswerPart:
			({
				messageId,
			}: {
				messageId: string;
			}): (({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ dispatch }) => {
				dispatch(resetHasReceivedAnswerPart({ messageId }));
			},
		addImplicitResearchPlan:
			({
				messageId,
				content,
				roundId,
			}: {
				messageId: string;
				content: string;
				roundId: number;
			}): (({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ dispatch }) => {
				dispatch(initResearchMessage({ messageId }));
				dispatch(
					addResearchPlan({
						messageId,
						content,
						title: 'Research Plan',
						roundId,
					}),
				);
			},
		processStreamResearch:
			({
				messageId,
				streamMessage,
				isDeepResearch = false,
			}: {
				messageId: string;
				streamMessage: StreamMessage;
				isDeepResearch?: boolean;
			}): (({ dispatch, getState }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ dispatch, getState }) => {
				// Guard fired RESPONSE_REDACTION: blank the accumulated response text that
				// streamed into each research round (the "answer above the response") while
				// keeping the step breakdown, so the unredacted text is not left in the store
				// (PRGD-488). Only routed here when the redaction feature gate is on.
				if (isStreamResponseRedaction(streamMessage)) {
					dispatch(clearResearchPlanContent({ messageId }));
					// The guard emits a trailing "Guard redacting" step AFTER FINAL_RESPONSE, which
					// flips researchCompleted back to false (see updateStep) and revives the "Rovo is
					// thinking" animation. The turn is over once we are redacting, so complete the
					// steps to settle research and stop the indicator (PRGD-488).
					dispatch(completeAllSteps({ messageId }));
				}

				// Check if this is a THINK_DEEPER_TRIGGERED trace
				if (isStreamThinkDeeperTriggered(streamMessage)) {
					dispatch(setHasThinkDeeperTriggered({ messageId, triggered: true }));
				}

				if (isStreamResearchPlan(streamMessage)) {
					dispatch(initResearchMessage({ messageId }));
					dispatch(
						addResearchPlan({
							messageId,
							content: streamMessage.message.content,
							title: 'Research Plan',
							roundId: streamMessage.message.roundId,
						}),
					);
				}

				if (isStreamAnswerPart(streamMessage)) {
					dispatch(markAnswerPartReceived({ messageId }));
				}

				if (isStreamResearchBeginningTrace(streamMessage)) {
					dispatch(initResearchMessage({ messageId }));
				}

				if (isStreamResearchStepContent(streamMessage) || isStreamReasoningPart(streamMessage)) {
					const { stepId, content } = streamMessage.message;
					const roundId = isStreamResearchStepContent(streamMessage)
						? streamMessage.message.roundId
						: (getState()[messageId]?.steps[stepId]?.roundId ?? 0);

					// append the stream chunk content to the correct step data
					dispatch(
						appendStepContent({
							messageId,
							stepId,
							roundId: Number(roundId),
							isStreamed: isStreamReasoningPart(streamMessage),
							// Show reasoning parts steps (e.g. "Creating a plan") immediately
							// to avoid artificial delays in UI from queued step logic
							immediate: isStreamReasoningPart(streamMessage),
							content,
						}),
					);
				}

				if (
					isStreamResearchStepTitle(streamMessage) ||
					isStreamResearchStepTitleShort(streamMessage)
				) {
					const { roundId, stepId, title, toolIcon, toolName } = streamMessage.message;
					const renderMode = isStreamResearchStepTitle(streamMessage)
						? streamMessage.message.renderMode
						: undefined;
					dispatch(
						setStepTitle({
							messageId,
							stepId,
							title,
							roundId: Number(roundId),
							immediate: false,
							toolIcon,
							toolName,
							renderMode,
						}),
					);
				}

				if (isStreamSkillStart(streamMessage)) {
					const { skill_status_metadata } = streamMessage.message;
					dispatch(
						setStepTitle({
							messageId,
							// random UUID since skills do not send stepIds, this should not matter for the step title
							stepId: '68df4a4e-a42b-4d77-851f-6ac5d91bf38a',
							title: skill_status_metadata?.aliases?.[0],
							roundId: 0,
							immediate: true,
							isSkill: true,
						}),
					);
				}

				if (isStreamSkillEnd(streamMessage)) {
					dispatch(
						updateStep({
							messageId,
							stepId: '12345',
							updater: (step) => ({ ...step, completed: true }),
						}),
					);
				}

				if (isStreamResearchStepSource(streamMessage)) {
					const {
						message: { sources, stepId },
					} = streamMessage;
					dispatch(
						setStepSources({
							messageId,
							stepId,
							sources,
						}),
					);
				}

				if (isStreamResearchStepIcons(streamMessage)) {
					const { products, stepId } = streamMessage.message;
					const ICON_BASE_URL = '/gateway/api/assist/rovo/v1/resources/icon/';
					dispatch(
						setStepSourceIcons({
							messageId,
							stepId,
							// Backend returns short names like "jira" — prepend the full gateway path.
							// If the path already starts with "/" or is a data URI, use it as-is.
							sourceIcons: products.map((product) =>
								product.startsWith('/') || product.startsWith('data:')
									? product
									: `${ICON_BASE_URL}${product}`,
							),
						}),
					);
				}

				if (isStreamResearchStepDocument(streamMessage)) {
					const { title, url, product, toolName, stepId } = streamMessage.message;
					dispatch(
						appendStepDocument({
							messageId,
							stepId,
							document: { stepId, title, url, product, toolName },
						}),
					);
				}

				if (isStreamResearchStepCompleted(streamMessage)) {
					const { stepId } = streamMessage.message;
					dispatch(
						updateStep({
							messageId,
							stepId,
							updater: (step) => ({ ...step, completed: true }),
						}),
					);
				}

				// Fail safe to complete all steps so the UI doesn't appear incomplete if there are missing chunks in the payload
				if (isStreamResponse(streamMessage)) {
					dispatch(completeAllSteps({ messageId }));
				}

				if (isDeepResearch) {
					dispatch(setIsDeepResearch({ messageId }));
				}
			},
		updateResearchMessageId:
			({
				messageId,
				newMessageId,
			}: {
				messageId: string;
				newMessageId: string;
			}): (({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ getState, setState }) => {
				setState({
					...getState(),
					[newMessageId]: getState()[messageId],
					[messageId]: undefined,
				});
			},
		incrementProgressArtificially:
			({
				messageId,
			}: {
				messageId: string;
			}): (({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ getState, setState }) => {
				const currentState = getState();
				const currentMessage = currentState[messageId];
				setState({
					...currentState,
					[messageId]: {
						...currentMessage,
						currentProgress: ceiledIncrement(
							currentMessage?.currentProgress ?? STARTING_PROGRESS,
							ARTIFICIAL_PROGRESS_RATE,
						),
					},
				});
			},
		markIncompleteResearchesErrored:
			({
				messageId,
			}: {
				messageId: string;
			}): (({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ getState, setState }) => {
				const currentState = getState();
				const message = currentState[messageId];
				if (
					message &&
					(message.currentProgress ?? 0) > 0 &&
					!message.researchCompleted &&
					!message.errored
				) {
					setState({
						...currentState,
						[messageId]: { ...message, errored: true },
					});
				}
			},
		completeAllSteps:
			({
				messageId,
			}: {
				messageId: string;
			}): (({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ dispatch }) => {
				dispatch(completeAllSteps({ messageId }));
			},
		getMessageResearchById:
			({
				messageId,
			}: {
				messageId: string;
			}): (({
				getState,
			}: StoreActionApi<ChatMessageResearchStore>) => MessageResearch | undefined) =>
			({ getState }): MessageResearch | undefined => {
				return getState()[messageId];
			},
		/**
		 * Clears all research data for a specific message.
		 * Used when Answer Now is triggered to reset the research state.
		 */
		clearMessageResearch:
			({
				messageId,
			}: {
				messageId: string;
			}): (({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void) =>
			({ getState, setState }) => {
				const currentState = getState();
				const { [messageId]: _, ...rest } = currentState;
				setState({
					[messageId]: undefined,
					...rest,
				});
			},
	},
});
